import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, from, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { ApiResponse, ApiResponseStatusCode, BoneViewEnum, Image, ImageCategorization, ImageCategorizationSave, ImageRepository, IntegrationRepository } from '../core';
import { Attachment, ImageCategorizationForm, ImageItem } from '../models';


/**
 * This Service handles attachment data.
 */
@Injectable({ providedIn: 'root' })
export class AttachmentService {

	private readonly imageRepo = inject(ImageRepository);
	private readonly intRepo = inject(IntegrationRepository);

	private apLoad = false;
	private readonly _apImageId = new BehaviorSubject<string | null>(null);
	private readonly _apImage = new BehaviorSubject<Attachment | null>(null);
	private _originalApImageId: string | null = null;
	apLoadingStatus = false;
	apProximalRingId: number | null = null;
	apDistalRingId: number | null = null;

	private ltLoad = false;
	private readonly _ltImageId = new BehaviorSubject<string | null>(null);
	private readonly _ltImage = new BehaviorSubject<Attachment | null>(null);
	private _originalLtImageId: string | null = null;
	ltLoadingStatus = false;
	ltProximalRingId: number | null = null;
	ltDistalRingId: number | null = null;

	private _imagePatientId: string | null = null;
	private _imageUserId: string | null = null;

	planImageAlreadyReset = false;

	/**
	 * Check if input image is the saved one.
	 */
	isOriginalImage(imageId: string | null, view: BoneViewEnum): boolean {
		return !!imageId && ((view === BoneViewEnum.AP && this._originalApImageId === imageId) || (view === BoneViewEnum.LAT && this._originalLtImageId === imageId));
	}

	/**
	 * Check if ring tool is disabled.
	 */
	isRingToolDisabled(view: BoneViewEnum): boolean {
		if (view === BoneViewEnum.LAT) {
			return !!this._apImageId.value && !this.apProximalRingId && !this.apDistalRingId;
		} else {
			return !!this._ltImageId.value && !this.ltProximalRingId && !this.ltDistalRingId;
		}
	}

	/**
	* Set current plan images.
	*/
	setPlanImages(patientId: string, userId: string, apImage: Attachment | null, ltImage: Attachment | null): void {
		this._imagePatientId = patientId;
		this._imageUserId = userId;
		// AP image
		this._originalApImageId = apImage?.id ?? null;
		this.apLoad = false;
		this._apImage.next(apImage);
		this._apImageId.next(apImage?.id ?? null);
		this.apProximalRingId = apImage?.ringProximalId ?? null;
		this.apDistalRingId = apImage?.ringDistalId ?? null;
		// LT image
		this._originalLtImageId = ltImage?.id ?? null;
		this.ltLoad = false;
		this._ltImage.next(ltImage);
		this._ltImageId.next(ltImage?.id ?? null);
		this.ltProximalRingId = ltImage?.ringProximalId ?? null;
		this.ltDistalRingId = ltImage?.ringDistalId ?? null;
	}

	/**
	* Reset current plan images.
	*/
	resetPlanImages(): void {
		this._imagePatientId = null;
		this._imageUserId = null;
		this._originalApImageId = null;
		this._originalLtImageId = null;
		this.setApImageId(null, false);
		this.setLtImageId(null, false);
		this.planImageAlreadyReset = false;
	}

	/**
	* Check if AP image is loading.
	*/
	isApLoading(): Observable<boolean> {
		return combineLatest([
			this._apImageId.asObservable(),
			this._apImage.asObservable(),
		]).pipe(
			map(([id, img]) => !!id && img?.id !== id),
			tap(status => this.apLoadingStatus = status)
		);
	}

	/**
	* Check if LT image is loading.
	*/
	isLtLoading(): Observable<boolean> {
		return combineLatest([
			this._ltImageId.asObservable(),
			this._ltImage.asObservable(),
		]).pipe(
			map(([id, img]) => !!id && img?.id !== id),
			tap(status => this.ltLoadingStatus = status)
		);
	}

	/**
	* Reset images loading status.
	*/
	resetLoading(imgId: string): void {
		if (imgId === this._apImageId.value) {
			this.setApImageId(imgId, true);
		} else if (imgId === this._ltImageId.value) {
			this.setLtImageId(imgId, true);
		}
	}

	/**
	* Set current ap image id.
	*/
	setApImageId(imageId: string | null, forceLoad: boolean, proxRingId?: number, distalRingId?: number): void {
		this.apLoad = forceLoad;
		this._apImage.next(null);
		this._apImageId.next(imageId);
		if (!forceLoad) {
			this.apProximalRingId = proxRingId ?? null;
			this.apDistalRingId = distalRingId ?? null;
		}
	}

	/**
	* Get current ap image.
	*/
	getApImage(): Observable<Attachment | null> {
		return this._apImage.asObservable().pipe(distinctUntilChanged());
	}

	/**
	 * Load ap image.
	 */
	loadApImage(): Observable<Attachment | null> {
		return this._apImageId.asObservable().pipe(
			switchMap(id => id && this.apLoad ? this.getThumbnail(id) : of(this._apImage.value)),
			tap(img => this._apImage.next(img))
		);
	}

	/**
	* Set current lateral image id.
	*/
	setLtImageId(imageId: string | null, forceLoad: boolean, proxRingId?: number, distalRingId?: number): void {
		this.ltLoad = forceLoad;
		this._ltImage.next(null);
		this._ltImageId.next(imageId);
		if (!forceLoad) {
			this.ltProximalRingId = proxRingId ?? null;
			this.ltDistalRingId = distalRingId ?? null;
		}
	}

	/**
	* Get current lateral image.
	*/
	getLtImage(): Observable<Attachment | null> {
		return this._ltImage.asObservable().pipe(distinctUntilChanged());
	}

	/**
	 * Load lateral image.
	 */
	loadLtImage(): Observable<Attachment | null> {
		return this._ltImageId.asObservable().pipe(
			switchMap(id => id && this.ltLoad ? this.getThumbnail(id) : of(this._ltImage.value)),
			tap(img => this._ltImage.next(img))
		);
	}

	/**
	 * Load images.
	 */
	loadImages(patId: string, userId: string, apId?: string | null, ltId?: string | null): Observable<[ap: Attachment | null, lt: Attachment | null]> {
		const imageIds = [];
		apId && imageIds.push(apId);
		ltId && imageIds.push(ltId);
		return this.imageRepo.getThumbnails(patId, userId, imageIds).pipe(
			map(res => this.handleImageResponse(res)),
			map(list => ([list?.find(i => i.id === apId) ?? null, list?.find(i => i.id === ltId) ?? null]))
		);
	}

	/**
	* Upload image file and data.
	* @param {Image} image Image file and data to upload.
	*/
	uploadImage(image: ImageItem): Observable<Image> {
		const imageData: ImageCategorizationSave = { ...image };
		return this.imageRepo.uploadImage(imageData, image.file!).pipe(
			map(res => this.handleImageResponse(res))
		);
	}

	/**
	* Edit image categorization data.
	* @param {Image} image Image data to edit.
	*/
	editImage(image: ImageItem): Observable<any> {
		const imageData = this.imageDataEditMapper(image);
		return this.intRepo.editImageCategorization(image.id!, imageData).pipe(
			map(res => this.handleImageResponse(res))
		);
	}

	/**
	 * Map ImageItem to ImageCategorization data.
	 */
	private imageDataEditMapper(image: ImageItem): ImageCategorization {
		return {
			patientId: image.patientId,
			boneType: image.boneType,
			boneView: image.boneView,
			boneSide: image.boneSide,
			scaleFactor: image.scaleFactor,
			orientation: image.orientation,
			referenceType: image.referenceType,
			circleCoordinates: image.circleCoordinates,
			ringProximalId: image.ringProximalId,
			ringDistalId: image.ringDistalId,
			ringMatrix: image.ringMatrix,
			angle: image.angle,
			frontal: image.frontal,
			axial: image.axial,
			brightness: image.brightness,
			contrast: image.contrast,
			planeRotation: image.planeRotation
		}
	}

	/**
	* Get image detail.
	* @param {string} imageId Image id.
	*/
	getImageDetail(imageId: string, userId: string): Observable<Image> {
		return this.imageRepo.getImageDetail(imageId, userId).pipe(
			map(res => this.handleImageResponse(res))
		);
	}

	private getThumbnail(imageId: string): Observable<Attachment | null> {
		return this.imageRepo.getThumbnails(this._imagePatientId!, this._imageUserId!, [imageId]).pipe(
			map(res => this.handleImageResponse(res)),
			map(list => list?.[0] ?? null)
		);
	}

	/**
	 * Init image to upload.
	 */
	initUploadImage(img: File): ImageItem {
		return {
			id: null,
			url: null,
			file: img,
			patientId: this._imagePatientId!,
			boneType: null,
			boneView: null,
			boneSide: null,
			scaleFactor: null,
			orientation: null
		};
	}

	/**
	 * Init image to edit.
	 */
	initEditImage(image: Image, file: File): ImageItem {
		return { ...image, file };
	}

	/**
	 * Get file from input URL.
	 */
	getFileByURL(url: string, fileName: string): Observable<File> {
		return from(fetch(url)).pipe(
			switchMap((response) => from(response.blob())),
			map(blob => new File([blob], fileName, { type: blob.type }))
		);
	}

	/**
	 * Update ImageItem object.
	 */
	updateImageItem(
		imageItem: ImageItem,
		categorizationForm?: ImageCategorizationForm
	): ImageItem {
		if (!categorizationForm) return imageItem;
		return {
			...imageItem,
			boneType: categorizationForm.boneType!,
			boneView: categorizationForm.boneView!,
			boneSide: categorizationForm.boneSide!,
			scaleFactor: categorizationForm.scaleFactor!,
			orientation: categorizationForm.orientation!,
			// Ring calibration
			referenceType: categorizationForm.ringCalibration?.referenceType ?? undefined,
			circleCoordinates: categorizationForm.ringCalibration?.ellipsePoints ?? undefined,
			ringProximalId: categorizationForm.ringCalibration?.proxRingId ?? undefined,
			ringDistalId: categorizationForm.ringCalibration?.distalRingId ?? undefined,
			ringMatrix: categorizationForm.ringCalibration?.ringMatrix ?? undefined,
			angle: categorizationForm.ringCalibration?.angle ?? undefined,
			frontal: categorizationForm.ringCalibration?.frontal ?? undefined,
			axial: categorizationForm.ringCalibration?.axial ?? undefined,
			// Edit tools
			brightness: categorizationForm.edit?.brightness ?? undefined,
			contrast: categorizationForm.edit?.contrast ?? undefined,
			planeRotation: categorizationForm.edit?.rotation ?? undefined
		};
	}

	/**
	 * Map ImageItem object to ImageCategorizationForm object.
	 */
	categorizationFormMapper(item: Image): ImageCategorizationForm {
		return {
			boneType: item.boneType,
			boneSide: item.boneSide,
			boneView: item.boneView,
			orientation: item.orientation,
			scaleFactor: item.scaleFactor,
			isRingCalibration: !!item.circleCoordinates?.length,
			ringCalibration: item.circleCoordinates?.length ? {
				referenceType: item.referenceType!,
				ellipsePoints: item.circleCoordinates,
				proxRingId: item.ringProximalId!,
				distalRingId: item.ringDistalId!,
				ringMatrix: item.ringMatrix!,
				angle: item.angle!,
				frontal: item.frontal!,
				axial: item.axial!
			} : null,
			edit: {
				brightness: item.brightness ?? null,
				contrast: item.contrast ?? null,
				rotation: item.planeRotation ?? null
			}
		};
	}

	private handleImageResponse<T>(response: ApiResponse<T>): T {
		if (response.statusCode == ApiResponseStatusCode.Success) {
			return response.result as T;
		} else {
			throw new Error('Generic error');
		}
	}

}